import React, { useState, useEffect, useMemo } from "react";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { makeGetPayPeriodItems } from "../../../../selectors/payPeriodSelectors";
import { makeGetFilteredContracts } from "../../../../selectors/contractSelector";
import { makeGetCandidate } from "../../../../selectors/candidateSelectors";
import moment from "moment";
import { round } from "lodash";
import { Row, Col } from "react-grid-system";
import {
  Table,
  TableHead,
  TableCell,
  TableContainer,
  TableBody,
  TableRow,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@material-ui/core";
import ActionModalContainer from "../../../General/ActionModal/ActionModalContainer";
import ActionModalHeader from "../../../General/ActionModal/ActionModalHeader";
import ActionModalContent from "../../../General/ActionModal/ActionModalContent";
import ActionModalFooter from "../../../General/ActionModal/ActionModalFooter";
import { reportTimesheetCorrection } from "../../../../actions/recruiters/payPeriodActions";
import { clearModal } from "../../../../actions/shared/uiActions";
import useSnackbar from "../../../General/useSnackbar";
import { useRevisePayPeriodModalStyles } from "../../../shared/PayPeriods/PayPeriodActions/styles";
import { StandardHours, AdditionalBilling } from "../../../shared/PayPeriods/tableColumns";
import { GET } from "../../../../util/apiHelpers";
import * as Yup from "yup";

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;
const formatPercentage = (value) => `${round(value * 100, 2)}%`;

const TOLERANCE = 0.01;

function timesheetsMatch(approvedHours, approvedPayout, approvedBill, revisedHours, revisedPayout, revisedBill) {
  return (
    Math.abs(parseFloat(approvedHours || 0) - parseFloat(revisedHours || 0)) < TOLERANCE &&
    Math.abs(parseFloat(approvedPayout || 0) - parseFloat(revisedPayout || 0)) < TOLERANCE &&
    Math.abs(parseFloat(approvedBill || 0) - parseFloat(revisedBill || 0)) < TOLERANCE
  );
}

const TABLE_COLUMNS = [
  { key: "label", label: "", align: "left" },
  { key: "hours", label: "Hours", align: "center" },
  { key: "markup", label: "Markup", align: "center" },
  { key: "additions", label: "Additions", align: "center" },
  { key: "workerOwed", label: "Worker Owed", align: "center" },
  { key: "billAmount", label: "Bill Amount", align: "center" },
];

const validationSchema = Yup.object().shape({
  correctionReason: Yup.string()
    .required("Correction reason is required")
    .test(
      "min-words",
      "Correction reason must be at least 3 words",
      (value) => value && value.trim().split(/\s+/).length >= 3
    ),
  isConfirmed: Yup.boolean().oneOf([true], "Please confirm the information is correct"),
});

function ReportTimesheetCorrectionModal({ payPeriodId }) {
  const classes = useRevisePayPeriodModalStyles();
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [temporaryUkgTimesheetData, setTemporaryUkgTimesheetData] = useState(null);
  const [loadingUkgData, setLoadingUkgData] = useState(true);

  const getPayPeriodItems = useMemo(() => makeGetPayPeriodItems(), []);
  const payPeriod = useSelector((state) => getPayPeriodItems(state)[payPeriodId]);

  const getFilteredContracts = useMemo(() => makeGetFilteredContracts(), []);
  const contract = useSelector(
    (state) =>
      getFilteredContracts(state, { contractId: payPeriod?.attributes?.contractId })[0]
  );

  const getCandidate = useMemo(() => makeGetCandidate(), []);
  const candidate = useSelector((state) =>
    getCandidate(state, contract?.attributes?.recruiterSubmissionId)
  );

  useEffect(() => {
    if (payPeriodId) {
      setLoadingUkgData(true);
      GET(`/api/v3/recruiter/pay_periods/${payPeriodId}/populate_temporary_ukg_timesheet`)
        .then((response) => {
          setTemporaryUkgTimesheetData(response.data);
          setLoadingUkgData(false);
        })
        .catch((error) => {
          console.error("Error fetching UKG timesheet data:", error);
          snackbar.showMessage("Unable to fetch revised timesheet data. Please try again.");
          setLoadingUkgData(false);
        });
    }
  }, [payPeriodId, snackbar]);

  if (!payPeriod || !candidate) {
    return (
      <ActionModalContainer color="red">
        <ActionModalHeader
          title="Report Timesheet Correction"
          subtitle="Error"
        />
        <ActionModalContent>
          <p>Unable to load pay period data. Please refresh the page and try again.</p>
        </ActionModalContent>
      </ActionModalContainer>
    );
  }

  if (loadingUkgData || !temporaryUkgTimesheetData) {
    return (
      <ActionModalContainer color="red">
        <ActionModalHeader
          title="Report Timesheet Correction"
          subtitle="Loading..."
        />
        <ActionModalContent>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress size={60} />
          </div>
        </ActionModalContent>
      </ActionModalContainer>
    );
  }

  const { lastName, firstName } = candidate.attributes;

  const {
    startDate,
    endDate,
    markupPercentage,
    standardHours,
    candidatePayoutAmount,
    employerCollectionAmount,
    customPayRatesData,
    additionalBilling: additionalBillingRaw,
    additionalBillingReason,
  } = payPeriod.attributes;

  const additionalBilling = Number(additionalBillingRaw) || 0;

  const {
    standard_hours,
    candidate_payout_amount,
    bill_amount,
    custom_pay_rates_data,
  } = temporaryUkgTimesheetData;

  const candidateName = `${firstName} ${lastName}`;
  const workWeek = `${moment.utc(startDate).format("ddd. MMM D")} - ${moment
    .utc(endDate)
    .format("ddd. MMM D")}`;

  const wfmMatchesApproved = timesheetsMatch(
    standardHours,
    candidatePayoutAmount,
    employerCollectionAmount,
    standard_hours,
    candidate_payout_amount,
    bill_amount
  );

  const tableData = [
    {
      label: "Approved Timesheet",
      hoursProps: {
        standardHours: round(parseFloat(standardHours || 0), 2).toString(),
        standardHoursDetails: null,
        customPayRatesData: customPayRatesData,
      },
      markup: formatPercentage(markupPercentage),
      additionsProps: {
        additionalBilling,
        additionalBillingReason,
      },
      workerOwed: formatCurrency(candidatePayoutAmount),
      billAmount: formatCurrency(employerCollectionAmount),
      bodyClass: classes.disputedTableBody,
    },
    {
      label: "Revised Timesheet",
      hoursProps: {
        standardHours: standard_hours,
        standardHoursDetails: null,
        customPayRatesData: custom_pay_rates_data,
      },
      markup: formatPercentage(markupPercentage),
      additionsProps: {
        additionalBilling,
        additionalBillingReason,
      },
      workerOwed: formatCurrency(candidate_payout_amount),
      billAmount: formatCurrency(bill_amount),
      bodyClass: classes.revisedTableBody,
    },
  ];

  const initialValues = {
    correctionReason: "",
    isConfirmed: false,
  };

  const handleSubmit = (values) => {
    setIsSubmitting(true);
    dispatch(reportTimesheetCorrection(payPeriodId, values.correctionReason, { approved: true }))
      .then(() => {
        snackbar.showMessage("Timesheet correction has been reported successfully");
        setIsSubmitting(false);
        dispatch(clearModal());
      })
      .catch((error) => {
        console.error(error);
        const errorMessage =
          error.response?.data?.errors?.[0] ||
          "An error occurred, try again or contact support.";
        snackbar.showMessage(errorMessage);
        setIsSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        submitCount,
      }) => (
        <ActionModalContainer color="red">
          <ActionModalHeader
            title="Report Timesheet Correction"
            subtitle={`${candidateName} - ${workWeek}`}
          />
          <ActionModalContent>
            <p className={classes.comparisonTitle}>
              Reporting a correction allows you to adjust timesheets that have
              already been invoiced.
            </p>
            <p>
              The Revised Timesheet below reflects the most up-to-date
              information in WFM for this timesheet. Please confirm its accuracy
              before submitting a correction. If the Revised Timesheet data is
              incorrect, contact the employer to update it in WFM.
            </p>
            <p>Corrections will appear as an addition or credit on the next invoice.</p>

            <Row style={{ marginTop: "24px", marginBottom: "24px" }}>
              <Col xs={3}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 600, letterSpacing: "0.4px", color: "#B0BEC5", textTransform: "uppercase", marginBottom: "8px", fontFamily: "'proxima-nova', helvetica" }}>
                    WORKER NAME
                  </label>
                  <div style={{ fontSize: "16px", color: "#37474F" }}>
                    {candidateName}
                  </div>
                </div>
              </Col>
              <Col xs={6}>
                <TextField
                  fullWidth
                  label="WHY ARE YOU REPORTING AN ISSUE?"
                  name="correctionReason"
                  value={values.correctionReason}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.correctionReason && !!errors.correctionReason}
                  helperText={
                    touched.correctionReason && errors.correctionReason
                      ? errors.correctionReason
                      : "Must be at least 3 words"
                  }
                  rows={3}
                  InputLabelProps={{ shrink: true, style: { marginTop: 3 } }}
                />
              </Col>
            </Row>

            <h5>Timesheet Comparison</h5>
            <br />
            <TableContainer classes={{ root: classes.tableContainer }}>
              <Table classes={{ root: classes.table }}>
                <TableHead classes={{ root: classes.tableHeader }}>
                  {TABLE_COLUMNS.map((column) => (
                    <TableCell key={column.key} align={column.align}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableHead>
                {tableData.map((rowData, index) => (
                  <TableBody key={index} classes={{ root: rowData.bodyClass }}>
                    <TableRow>
                      {TABLE_COLUMNS.map((column) => {
                        let cellContent;
                        if (column.key === "hours") {
                          cellContent = <StandardHours {...rowData.hoursProps} />;
                        } else if (column.key === "additions") {
                          cellContent = <AdditionalBilling {...rowData.additionsProps} />;
                        } else {
                          cellContent = rowData[column.key];
                        }
                        return (
                          <TableCell key={column.key} align={column.align}>
                            {cellContent}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableBody>
                ))}
              </Table>
            </TableContainer>
            <Row className={classes.checkboxRow}>
              <Col>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isConfirmed}
                      onChange={(e) => setFieldValue("isConfirmed", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="The above information is correct for this timesheet."
                />
              </Col>
            </Row>
          </ActionModalContent>
          <ActionModalFooter
            actionText="Submit"
            handleAction={handleSubmit}
            isSubmitting={isSubmitting}
            disabled={wfmMatchesApproved}
            submissionError={
              (submitCount > 0 && Object.keys(errors).length > 0) ||
              (submitCount > 0 && !values.isConfirmed)
            }
            customErrorMessage={
              submitCount > 0 && !values.isConfirmed
                ? "Please confirm the information is correct."
                : submitCount > 0 && errors.correctionReason
                  ? errors.correctionReason
                  : ""
            }
          />
        </ActionModalContainer>
      )}
    </Formik>
  );
}

export default ReportTimesheetCorrectionModal;
